import {
  API_ROUTES,
  adjustmentSchema,
  chartAccountSchema,
  gifiRowSchema,
  openingBalancesSchema,
  openingFromGifiRowSchema,
  type OpeningFromGifiRequest,
  type RecordAdjustmentRequest,
  type SetAccountGifiRequest,
  type SetOpeningBalancesRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

export const chartQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['ledger', organizationId, 'chart'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.ledger.chart(organizationId),
        schema: z.array(chartAccountSchema),
        signal,
      }),
    staleTime: 60_000,
  });

export const openingBalancesQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['ledger', organizationId, 'opening'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.ledger.openingBalances(organizationId),
        schema: openingBalancesSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export const gifiQuery = (organizationId: string, from: string, to: string) =>
  queryOptions({
    queryKey: ['reports', organizationId, 'gifi', from, to] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.reports.gifi(organizationId)}?from=${from}&to=${to}`,
        schema: z.array(gifiRowSchema),
        signal,
      }),
    staleTime: 60_000,
  });

export const setAccountGifi = (
  organizationId: string,
  accountId: string,
  body: SetAccountGifiRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.ledger.accountGifi(organizationId, accountId),
    schema: z.undefined(),
    body,
  });

export const setOpeningBalances = (organizationId: string, body: SetOpeningBalancesRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.ledger.openingBalances(organizationId),
    schema: z.object({ entryId: z.string().nullable() }),
    body,
  });

export const adjustmentsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['ledger', organizationId, 'adjustments'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.ledger.adjustments(organizationId),
        schema: z.array(adjustmentSchema),
        signal,
      }),
    staleTime: 30_000,
  });

export const recordAdjustment = (organizationId: string, body: RecordAdjustmentRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.ledger.adjustments(organizationId),
    schema: z.object({ entryId: z.string().nullable() }),
    body,
  });

export const reverseAdjustment = (organizationId: string, entryId: string, reason: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.ledger.reverseAdjustment(organizationId, entryId),
    schema: z.object({ entryId: z.string().nullable() }),
    body: { reason },
  });

export const openingFromGifi = (organizationId: string, body: OpeningFromGifiRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.ledger.openingFromGifi(organizationId),
    schema: z.array(openingFromGifiRowSchema),
    body,
  });
